/**
 * @fileoverview Client-side error boundary for resource and generator tools.
 * @description Catches render errors in interactive resource subtrees, logs them
 * to BetterStack via `handleClientError` (which classifies the fault and may
 * auto-recover transient chunk faults), and mirrors non-deduped failures to GA4.
 *
 * @module components/resources/ResourceErrorBoundary
 */

'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'

import { handleClientError } from '~/lib/client-error-handling'

/**
 * Props for {@link ResourceErrorBoundary}.
 *
 * @interface ResourceErrorBoundaryProps
 * @property {ReactNode} children - Subtree to protect.
 * @property {ReactNode} [fallback] - Optional custom fallback UI when an error is caught.
 */
interface Props {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * State tracked after a child render error is caught.
 *
 * @interface ResourceErrorBoundaryState
 * @property {boolean} hasError - Whether an error has been caught.
 * @property {Error} [error] - The caught error instance.
 */
interface State {
  hasError: boolean
  error?: Error
}

/**
 * Error boundary for generator and resource interactive tools.
 *
 * @class ResourceErrorBoundary
 * @extends {Component<ResourceErrorBoundaryProps, ResourceErrorBoundaryState>}
 */
export class ResourceErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    }
  }

  /**
   * Logs boundary crashes to BetterStack and GA4 when not deduped.
   *
   * @method componentDidCatch
   * @memberof ResourceErrorBoundary
   * @instance
   * @param {Error} error - The error thrown by a child component.
   * @param {ErrorInfo} errorInfo - React-provided component stack metadata.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // GA4 mirroring happens inside `logClientError` (via handleClientError)
    // AFTER the drain confirms the ingest, tagged with this component name.
    handleClientError(
      error,
      {
        type: 'error_boundary',
        component: 'ResourceErrorBoundary',
        componentStack: errorInfo.componentStack,
      },
      { boundaryRecover: true },
    )
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-amber-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V14M12 17.5V18M6.6 21H17.4C18.8359 21 19.5544 21 20.0927 20.7478C20.5691 20.5219 20.9531 20.1524 21.1919 19.6953C21.4583 19.1814 21.4821 18.4787 21.5297 17.0732L21.9257 5.07324C21.9573 4.09428 21.9731 3.6048 21.8195 3.22264C21.6845 2.88283 21.4434 2.59944 21.1312 2.41288C20.7777 2.2 20.2856 2.2 19.3013 2.2H4.69872C3.71441 2.2 3.22225 2.2 2.86883 2.41288C2.55657 2.59944 2.31547 2.88283 2.18051 3.22264C2.02689 3.6048 2.04274 4.09428 2.07444 5.07324L2.47034 17.0732C2.51791 18.4787 2.54169 19.1814 2.80811 19.6953C3.04691 20.1524 3.43092 20.5219 3.90727 20.7478C4.44561 21 5.16414 21 6.6 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Unable to load resource
              </h3>
              {this.state.error && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {this.state.error.message}
                </p>
              )}
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                Try reloading
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
